import { useState } from 'react';
import MonthlyEventModal from './MonthlyEventModal';

export default function Monthly({
  calendar,
  user,
  demoMode,
  trads,
  lang,
  langUrl,
  getDayName,
  onEventSave,
  onEventDelete,
  recurring = false,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);

  const displayMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const today = new Date();
  const currentRealMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const isCurrentMonthOrPast = displayMonth <= currentRealMonth;

  const prevMonth = () => {
    if (!isCurrentMonthOrPast) {
      setCurrentDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
      );
    }
  };

  let startDayOfWeek = displayMonth.getDay() - 1;
  if (startDayOfWeek === -1) startDayOfWeek = 6;

  const daysInMonth = new Date(
    displayMonth.getFullYear(),
    displayMonth.getMonth() + 1,
    0
  ).getDate();

  const cells = [];
  for (let i = 0; i < 42; i++) {
    const dayNumber = i - startDayOfWeek + 1;
    if (dayNumber > 0 && dayNumber <= daysInMonth) {
      cells.push(dayNumber);
    } else if (dayNumber === daysInMonth + 1 && recurring) {
      cells.push(32);
    } else {
      cells.push(null);
    }
  }

  const getEventsForDay = (dom) => {
    if (!calendar?.events) return [];
    return calendar.events.filter((e) => e.daysInMonth?.includes(dom));
  };

  const handleCellClick = (dom) => {
    if (!dom) return;
    setSelectedEvent({
      id: null,
      type: 'monthly',
      name: '',
      content: '',
      daysInMonth: [dom],
      times: [540],
      groupIds: [],
      eventContacts: [],
    });
  };

  const tIndex = trads?.index || {};
  const tMonths = tIndex.months || {};

  const monthNames = [
    tMonths['0'] || 'January',
    tMonths['1'] || 'February',
    tMonths['2'] || 'March',
    tMonths['3'] || 'April',
    tMonths['4'] || 'May',
    tMonths['5'] || 'June',
    tMonths['6'] || 'July',
    tMonths['7'] || 'August',
    tMonths['8'] || 'September',
    tMonths['9'] || 'October',
    tMonths['10'] || 'November',
    tMonths['11'] || 'December',
  ];

  return (
    <div className="mb-8">
      {recurring ? (
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {tIndex.monthlyRecurringEvents || 'Monthly recurring events'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {tIndex.monthlyRecurringEventsDescription ||
              'Pick exact days in the month.'}
          </p>
        </div>
      ) : (
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={prevMonth}
            disabled={isCurrentMonthOrPast}
            className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h2 className="text-xl font-bold text-gray-900 flex items-baseline gap-2">
            <span>
              {`${monthNames[displayMonth.getMonth()]} ${displayMonth.getFullYear()}`}
            </span>
          </h2>
          <button
            onClick={nextMonth}
            className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      )}

      <>
        <div className="grid grid-cols-7 gap-2">
          {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => (
            <div
              key={dayIndex}
              className="font-semibold text-center py-2 text-gray-600 text-sm tracking-wider"
            >
              {getDayName(dayIndex)}
            </div>
          ))}

          {cells.map((dom, i) => {
            if (dom === 32) {
              const dayEvents = getEventsForDay(dom);
              return (
                <div
                  key="last-day"
                  data-day="32"
                  onClick={() => handleCellClick(dom)}
                  className={`min-h-[120px] border border-gray-100 rounded-lg p-3 transition-colors cursor-pointer bg-white hover:bg-indigo-50/50 hover:border-indigo-100`}
                >
                  <div className="font-medium text-gray-500 mb-2 text-xs leading-tight">
                    {tIndex.lastDayOfTheMonth || 'Last day of the month'}
                  </div>
                  <div className="flex flex-col gap-1">
                    {dayEvents.map((evt) => (
                      <div
                        key={evt.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEvent(evt);
                        }}
                        className="bg-amber-50 border border-amber-100 text-amber-950 text-xs px-2 py-1.5 rounded-md truncate font-medium shadow-sm cursor-pointer hover:bg-amber-100 transition-colors"
                      >
                        {evt.name || tIndex.eventPlaceholder || 'Event'}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            const dayEvents = dom ? getEventsForDay(dom) : [];
            return (
              <div
                key={i}
                data-day={dom ? String(dom).padStart(2, '0') : undefined}
                onClick={() => handleCellClick(dom)}
                className={`min-h-[120px] border border-gray-100 rounded-lg p-3 transition-colors ${
                  dom
                    ? 'cursor-pointer bg-white hover:bg-indigo-50/50 hover:border-indigo-100'
                    : 'bg-gray-50/50'
                }`}
              >
                {dom && (
                  <div className="font-medium text-gray-500 mb-2">{dom}</div>
                )}
                <div className="flex flex-col gap-1">
                  {dayEvents.map((evt) => (
                    <div
                      key={evt.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEvent(evt);
                      }}
                      className="bg-amber-50 border border-amber-100 text-amber-950 text-xs px-2 py-1.5 rounded-md truncate font-medium shadow-sm cursor-pointer hover:bg-amber-100 transition-colors"
                    >
                      {evt.name || tIndex.eventPlaceholder || 'Event'}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </>

      {selectedEvent && (
        <MonthlyEventModal
          event={selectedEvent}
          isOpen={!!selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onSave={onEventSave}
          onDelete={onEventDelete}
          langUrl={langUrl}
          demoMode={demoMode}
          trads={trads}
          monthName={monthNames[displayMonth.getMonth()]}
        />
      )}
    </div>
  );
}
